import { supabase } from '../lib/supabase';
import {
  authenticateWithMicrosoft,
  fetchCalendarEmail,
  fetchOutlookCalendarEvents,
  convertOutlookEventsToAvailability,
  getCalendarConnection as getOutlookConnection,
  disconnectCalendar as disconnectOutlookCalendar,
  isTokenExpired,
} from './outlookCalendarService';
import {
  authenticateWithGoogle,
  getCalendarConnection as getGoogleConnection,
  disconnectGoogleCalendar,
} from './googleCalendarService';
import { saveUserAvailability, getUserAvailability, convertAvailableToBusyTimes } from './availabilityService';
import { calendarSyncConfig } from './msalConfig';
import { isNative } from './platformService';

/**
 * Detect if running in iOS standalone mode (PWA saved to home screen)
 */
const isIOSStandalone = () => {
  if (isNative()) return false;
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
};

/**
 * Check if we need to open a popup for OAuth (web browser flow)
 */
const needsPopupFlow = () => {
  return !isNative() && !isIOSStandalone();
};

/**
 * Open OAuth popup synchronously with loading state
 * MUST be called synchronously from click handler to avoid Safari popup blocker
 */
const openOAuthPopup = (provider) => {
  const width = 500;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    'about:blank',
    `${provider}-oauth`,
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
  );

  if (popup) {
    const brandColor = provider === 'google' ? '#4285f4' : '#0078d4';
    const providerName = provider === 'google' ? 'Google' : 'Microsoft';
    popup.document.write(`
      <!DOCTYPE html>
      <html>
        <head><title>Connecting to ${providerName}...</title></head>
        <body style="display:flex;justify-content:center;align-items:center;height:100vh;margin:0;font-family:system-ui,-apple-system,sans-serif;background:#f5f5f5;">
          <div style="text-align:center;">
            <div style="width:40px;height:40px;border:3px solid #e0e0e0;border-top-color:${brandColor};border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 16px;"></div>
            <p style="color:#666;margin:0;">Connecting to ${providerName}...</p>
          </div>
          <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
        </body>
      </html>
    `);
  }

  return popup;
};

/**
 * Get calendar connection for any provider
 * Returns the first connected calendar (Outlook or Google)
 */
export const getCalendarConnection = async (provider = null) => {
  if (provider === 'outlook') {
    return await getOutlookConnection('outlook');
  }
  if (provider === 'google') {
    return await getGoogleConnection('google');
  }

  // Check both providers, return whichever is connected
  const outlookConnection = await getOutlookConnection('outlook');
  if (outlookConnection) return outlookConnection;

  const googleConnection = await getGoogleConnection('google');
  return googleConnection;
};

/**
 * Get the currently connected provider (if any)
 */
export const getConnectedProvider = async () => {
  const outlookConnection = await getOutlookConnection('outlook');
  if (outlookConnection) return 'outlook';

  const googleConnection = await getGoogleConnection('google');
  if (googleConnection) return 'google';

  return null;
};

/**
 * Connect to calendar provider
 *
 * Flow (Confidential Client OAuth):
 * 1. Check if another provider is connected - disconnect it first (single provider only)
 * 2. Popup opens to provider login (Microsoft/Google)
 * 3. User authenticates, provider redirects to callback with auth code
 * 4. Callback page sends code to oauth-exchange edge function
 * 5. Server exchanges code for tokens using client_secret
 * 6. Tokens encrypted and stored - never seen by browser
 * 7. Delete manual availability (calendar becomes single source of truth)
 * 8. Log the connection
 */
export const connectCalendar = async (provider = 'outlook') => {
  // CRITICAL: Open popup SYNCHRONOUSLY at the start, before ANY async operations
  // Safari blocks popups that aren't in the direct click handler call stack
  let popup = null;
  if (needsPopupFlow()) {
    popup = openOAuthPopup(provider);
    if (!popup) {
      return {
        success: false,
        error: 'Failed to open popup. Please allow popups for this site.'
      };
    }
  }

  try {
    // Now we can do async operations - popup is already open
    // Check if another provider is already connected
    const currentProvider = await getConnectedProvider();
    if (currentProvider && currentProvider !== provider) {
      console.log(`Disconnecting ${currentProvider} before connecting ${provider}...`);
      await disconnectCalendar(currentProvider);
    }

    if (provider === 'outlook') {
      // Authenticate with Microsoft - tokens are stored via edge function
      // Pass the pre-opened popup to avoid Safari blocker
      const authResponse = await authenticateWithMicrosoft(popup);

      // authResponse now includes calendarEmail and connectionId from edge function
      const { calendarEmail, connectionId } = authResponse;

      // Delete all manual availability records - calendar becomes single source of truth
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        console.log('Deleting all manual availability - calendar is now single source of truth');
        const { error: deleteError } = await supabase
          .from('user_availability')
          .delete()
          .eq('user_id', user.id)
          .eq('is_calendar_synced', false);

        if (deleteError) {
          console.error('Error deleting manual availability:', deleteError);
          // Don't fail the connection if delete fails, just log it
        }
      }

      // Log the connection
      if (connectionId) {
        await logCalendarSync(connectionId, 'started', 'Initial connection established');
      }

      // Create webhook subscription for real-time calendar sync
      try {
        console.log('Creating webhook subscription for real-time calendar sync...');
        const { data: subData, error: subError } = await supabase.functions.invoke('manage-graph-subscription', {
          body: { action: 'create' }
        });

        if (subError) {
          console.warn('Failed to create webhook subscription:', subError);
          // Don't fail the connection - fallback to polling sync
        } else if (subData?.success) {
          console.log('Webhook subscription created:', subData.subscriptionId);
        }
      } catch (webhookError) {
        console.warn('Webhook subscription error (non-fatal):', webhookError);
        // Don't fail the connection - fallback to polling sync
      }

      return {
        success: true,
        connection: { id: connectionId, calendar_email: calendarEmail },
        message: `Successfully connected to ${calendarEmail}`
      };
    }

    if (provider === 'google') {
      // Authenticate with Google - tokens are stored via edge function
      // Pass the pre-opened popup to avoid Safari blocker
      const authResponse = await authenticateWithGoogle(popup);

      // authResponse includes calendarEmail and connectionId from edge function
      const { calendarEmail, connectionId } = authResponse;

      // Delete all manual availability records - calendar becomes single source of truth
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        console.log('Deleting all manual availability - Google calendar is now single source of truth');
        const { error: deleteError } = await supabase
          .from('user_availability')
          .delete()
          .eq('user_id', user.id)
          .eq('is_calendar_synced', false);

        if (deleteError) {
          console.error('Error deleting manual availability:', deleteError);
          // Don't fail the connection if delete fails, just log it
        }
      }

      // Log the connection
      if (connectionId) {
        await logCalendarSync(connectionId, 'started', 'Initial Google connection established');
      }

      // Create webhook subscription for real-time calendar sync
      try {
        console.log('Creating Google watch channel for real-time calendar sync...');
        const { data: subData, error: subError } = await supabase.functions.invoke('manage-google-subscription', {
          body: { action: 'create' }
        });

        if (subError) {
          console.warn('Failed to create Google watch channel:', subError);
          // Don't fail the connection - fallback to polling sync
        } else if (subData?.success) {
          console.log('Google watch channel created:', subData.channelId);
        }
      } catch (webhookError) {
        console.warn('Google watch channel error (non-fatal):', webhookError);
        // Don't fail the connection - fallback to polling sync
      }

      return {
        success: true,
        connection: { id: connectionId, calendar_email: calendarEmail },
        message: `Successfully connected to ${calendarEmail}`
      };
    }

    throw new Error(`Unsupported calendar provider: ${provider}`);
  } catch (error) {
    console.error('Error connecting calendar:', error);
    // Close popup on error if it was opened
    if (popup && !popup.closed) {
      try { popup.close(); } catch { /* ignore */ }
    }
    return {
      success: false,
      error: error.message || 'Failed to connect calendar'
    };
  }
};

// Sync calendar events to availability
export const syncCalendarToAvailability = async (startDate, endDate, options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get calendar connection (any provider)
    const connection = await getCalendarConnection();
    if (!connection) {
      throw new Error('No calendar connection found. Please connect your calendar first.');
    }

    if (!connection.sync_enabled) {
      throw new Error('Calendar sync is disabled. Enable it in your settings.');
    }

    const provider = connection.provider;

    // Note: Token refresh is now handled by the edge function
    // We just need to call the sync endpoint

    // Create sync log entry
    const syncLog = await logCalendarSync(connection.id, 'started', null, {
      sync_start_date: startDate,
      sync_end_date: endDate
    });

    try {
      // Use edge function for sync operation based on provider
      // Pass sync settings to ensure all-day events and other preferences are respected
      const syncSettings = connection.sync_settings || calendarSyncConfig;

      const functionName = provider === 'google' ? 'google-sync-calendar' : 'outlook-sync-calendar';
      const { data: syncResult, error: syncError } = await supabase.functions.invoke(functionName, {
        body: {
          action: 'sync',
          startDate,
          endDate,
          syncSettings
        }
      });

      if (syncError) {
        // Log detailed error info for debugging
        console.error('Sync Edge Function error:', {
          message: syncError.message,
          context: syncError.context,
          name: syncError.name,
          fullError: syncError
        });

        // Try to get the actual error from the response body
        let errorMessage = syncError.message || 'Sync failed';
        if (syncError.context instanceof Response) {
          try {
            const errorBody = await syncError.context.json();
            console.error('Edge Function error body:', errorBody);
            errorMessage = errorBody.error || errorBody.message || errorMessage;
          } catch {
            // Response body may already be consumed or not JSON
            console.error('Could not parse error response body');
          }
        }
        throw new Error(errorMessage);
      }

      // Also check if the response indicates an error (non-2xx returned error in data)
      if (syncResult?.error) {
        console.error('Sync returned error in response:', syncResult);
        throw new Error(syncResult.error);
      }

      // Update last synced timestamp
      await supabase
        .from('calendar_connections')
        .update({ last_synced_at: new Date().toISOString() })
        .eq('id', connection.id);

      // Complete sync log
      await logCalendarSync(connection.id, 'completed', null, {
        events_synced: syncResult.eventsProcessed || 0
      }, syncLog.id);

      return {
        success: true,
        eventsProcessed: syncResult.eventsProcessed || 0,
        message: syncResult.message || 'Calendar synced successfully',
      };

    } catch (syncError) {
      // Log sync failure
      await logCalendarSync(connection.id, 'failed', syncError.message, null, syncLog.id);
      throw syncError;
    }

  } catch (error) {
    console.error('Calendar sync error:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync calendar'
    };
  }
};

// Convert busy times to available times for a specific date
const convertBusyToAvailableForDate = (busyRanges) => {
  if (!busyRanges || busyRanges.length === 0) {
    // If no busy times, entire day is available
    return [{ start: '00:00', end: '24:00' }];
  }

  const golfDayStart = 0 * 60;   // midnight in minutes
  const golfDayEnd = 24 * 60;    // end of day in minutes
  
  // Convert busy ranges to minutes and sort
  const busyMinutes = busyRanges
    .map(range => ({
      start: timeToMinutes(range.start),
      end: timeToMinutes(range.end),
      ...range
    }))
    .sort((a, b) => a.start - b.start);
  
  const availableRanges = [];
  let currentTime = golfDayStart;
  
  for (const busy of busyMinutes) {
    if (busy.start > currentTime) {
      // Found available time before this busy period
      availableRanges.push({
        start: minutesToTime(currentTime),
        end: minutesToTime(busy.start)
      });
    }
    currentTime = Math.max(currentTime, busy.end);
  }
  
  // Check for available time after last busy period
  if (currentTime < golfDayEnd) {
    availableRanges.push({
      start: minutesToTime(currentTime),
      end: minutesToTime(golfDayEnd)
    });
  }
  
  return availableRanges;
};

// Helper functions for time conversion
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

// Merge availability data with different strategies
const mergeAvailabilityData = (existing, newData, strategy = 'merge') => {
  const merged = { ...existing };
  
  switch (strategy) {
    case 'replace':
      // Replace existing with new data
      Object.keys(newData).forEach(date => {
        merged[date] = newData[date];
      });
      break;
      
    case 'merge':
    default:
      // Merge new data with existing, combining ranges
      Object.keys(newData).forEach(date => {
        if (merged[date]) {
          // Combine and merge overlapping ranges
          const combined = [...merged[date], ...newData[date]];
          merged[date] = mergeOverlappingRanges(combined);
        } else {
          merged[date] = newData[date];
        }
      });
      break;
  }
  
  return merged;
};

// Merge overlapping time ranges
const mergeOverlappingRanges = (ranges) => {
  if (ranges.length <= 1) return ranges;
  
  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
  const merged = [sorted[0]];
  
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    
    const lastEndMinutes = timeToMinutes(last.end);
    const currStartMinutes = timeToMinutes(current.start);
    
    if (currStartMinutes <= lastEndMinutes) {
      const currEndMinutes = timeToMinutes(current.end);
      if (currEndMinutes > lastEndMinutes) {
        last.end = current.end;
      }
    } else {
      merged.push(current);
    }
  }
  
  return merged;
};

// Get calendar sync status
export const getCalendarSyncStatus = async () => {
  try {
    // Check for any connected calendar (Outlook or Google)
    const connection = await getCalendarConnection();

    if (!connection) {
      return {
        connected: false,
        provider: null,
        lastSynced: null,
        syncEnabled: false
      };
    }

    // Get recent sync logs
    const { data: syncLogs } = await supabase
      .from('calendar_sync_logs')
      .select('*')
      .eq('connection_id', connection.id)
      .order('created_at', { ascending: false })
      .limit(5);

    // Get webhook subscription status
    const { data: { user } } = await supabase.auth.getUser();
    let webhookStatus = null;
    if (user) {
      if (connection.provider === 'outlook') {
        const { data: subscription } = await supabase
          .from('graph_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (subscription) {
          webhookStatus = {
            active: true,
            subscriptionId: subscription.subscription_id,
            expiresAt: subscription.expiration_datetime,
            lastNotification: subscription.last_notification_at,
            notificationCount: subscription.notification_count || 0,
          };
        }
      } else if (connection.provider === 'google') {
        const { data: subscription } = await supabase
          .from('google_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (subscription) {
          webhookStatus = {
            active: true,
            channelId: subscription.channel_id,
            expiresAt: new Date(subscription.expiration).toISOString(),
            lastNotification: subscription.last_notification_at,
            notificationCount: subscription.notification_count || 0,
          };
        }
      }
    }

    return {
      connected: true,
      provider: connection.provider,
      calendarEmail: connection.calendar_email,
      lastSynced: connection.last_synced_at,
      syncEnabled: connection.sync_enabled,
      syncSettings: connection.sync_settings,
      recentSyncs: syncLogs || [],
      tokenExpiresAt: connection.token_expires_at,
      needsReauth: isTokenExpired(connection.token_expires_at),
      webhook: webhookStatus,
    };
  } catch (error) {
    console.error('Error getting calendar sync status:', error);
    return {
      connected: false,
      provider: null,
      lastSynced: null,
      syncEnabled: false,
      error: error.message
    };
  }
};

// Update sync settings
export const updateCalendarSyncSettings = async (settings) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get the connected provider
    const provider = await getConnectedProvider();
    if (!provider) throw new Error('No calendar connected');

    const { data, error } = await supabase
      .from('calendar_connections')
      .update({
        sync_settings: settings,
        sync_enabled: settings.enabled !== false
      })
      .eq('user_id', user.id)
      .eq('provider', provider)
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating sync settings:', error);
    throw error;
  }
};

// Disconnect calendar
export const disconnectCalendar = async (provider = 'outlook') => {
  try {
    // Delete webhook subscription first
    if (provider === 'outlook') {
      try {
        console.log('Deleting Outlook webhook subscription...');
        const { data: subData, error: subError } = await supabase.functions.invoke('manage-graph-subscription', {
          body: { action: 'delete' }
        });

        if (subError) {
          console.warn('Failed to delete Outlook webhook subscription:', subError);
        } else if (subData?.success) {
          console.log('Outlook webhook subscription deleted');
        }
      } catch (webhookError) {
        console.warn('Outlook webhook deletion error (non-fatal):', webhookError);
      }
    }

    if (provider === 'google') {
      try {
        console.log('Deleting Google watch channel...');
        const { data: subData, error: subError } = await supabase.functions.invoke('manage-google-subscription', {
          body: { action: 'delete' }
        });

        if (subError) {
          console.warn('Failed to delete Google watch channel:', subError);
        } else if (subData?.success) {
          console.log('Google watch channel deleted');
        }
      } catch (webhookError) {
        console.warn('Google watch channel deletion error (non-fatal):', webhookError);
      }
    }

    // Remove calendar-synced availability entries
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('user_availability')
        .delete()
        .eq('user_id', user.id)
        .eq('is_calendar_synced', true)
        .eq('calendar_provider', provider);
    }

    // Disconnect from provider
    if (provider === 'google') {
      await disconnectGoogleCalendar();
    } else {
      await disconnectOutlookCalendar(provider);
    }

    return {
      success: true,
      message: 'Calendar disconnected successfully'
    };
  } catch (error) {
    console.error('Error disconnecting calendar:', error);
    return {
      success: false,
      error: error.message || 'Failed to disconnect calendar'
    };
  }
};

// Log calendar sync activity
const logCalendarSync = async (connectionId, status, errorMessage = null, additionalData = {}, logId = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const logData = {
      connection_id: connectionId,
      user_id: user.id,
      sync_status: status,
      error_message: errorMessage,
      ...additionalData
    };
    
    if (logId && status !== 'started') {
      // Update existing log
      const { data, error } = await supabase
        .from('calendar_sync_logs')
        .update(logData)
        .eq('id', logId)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    } else {
      // Create new log
      const { data, error } = await supabase
        .from('calendar_sync_logs')
        .insert(logData)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Error logging calendar sync:', error);
    return null;
  }
};